import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card } from "react-bootstrap";
import {
  IconCalendar,
  IconUsers,
  IconMessage,
  IconTemplate,
  IconRobot,
  IconUser,
  IconHeadset,
  IconChartFunnel,
  IconTags,
  IconSettings,
  IconBrandWhatsapp,
  IconChartBar,
  IconArrowRight,
  IconWaveSawTool,
  IconBulb,
  IconStar,
  IconHeart,
} from "@tabler/icons-react";
import "./css/welcomeScreen.css";
import UserContext from "../../contexts/UserContext";

const WelcomeScreen = () => {
  const { user, userPermissions } = useContext(UserContext);
  const navigate = useNavigate();

  // Mapear módulos para rotas e ícones
  const moduleConfig = useMemo(
    () => ({
      Dashboard: {
        route: "/",
        icon: IconChartBar,
        color: "linear-gradient(135deg, #305CDE, #1ea7c5)",
        description: "Visão geral do sistema",
        category: "principal",
      },
      Chat: {
        route: "/chat",
        icon: IconMessage,
        color: "linear-gradient(135deg, #2bc155, #20c997)",
        description: "Conversas e mensagens",
        category: "comunicacao",
      },
      Agenda: {
        route: "/calendar",
        icon: IconCalendar,
        color: "linear-gradient(135deg, #ff8c00, #ffc107)",
        description: "Agendamentos e compromissos",
        category: "principal",
      },
      "Base de Clientes": {
        route: "/customers",
        icon: IconUsers,
        color: "linear-gradient(135deg, #de3096, #e83e8c)",
        description: "Gestão de clientes",
        category: "clientes",
      },
      Templates: {
        route: "/templates",
        icon: IconTemplate,
        color: "linear-gradient(135deg, #6f42c1, #8e44ad)",
        description: "Modelos de mensagens",
        category: "comunicacao",
      },
      "Meus Agentes": {
        route: "/agents",
        icon: IconRobot,
        color: "linear-gradient(135deg, #1ea7c5, #305CDE)",
        description: "Agentes de IA",
        category: "ia",
      },
      Campanhas: {
        route: "/campaigns",
        icon: IconBrandWhatsapp,
        color: "linear-gradient(135deg, #25D366, #128C7E)",
        description: "Campanhas de marketing",
        category: "marketing",
      },
      Publicações: {
        route: "/campaigns",
        icon: IconBrandWhatsapp,
        color: "linear-gradient(135deg, #25D366, #128C7E)",
        description: "Publicações e conteúdo",
        category: "marketing",
      },
      Automações: {
        route: "/automations",
        icon: IconWaveSawTool,
        color: "linear-gradient(135deg, #fd7e14, #e8590c)",
        description: "Fluxos automatizados",
        category: "automacao",
      },
      "Pipeline CRM": {
        route: "/crm",
        icon: IconChartFunnel,
        color: "linear-gradient(135deg, #dc3545, #c82333)",
        description: "Funil de vendas",
        category: "vendas",
      },
      "Tags & Categorias": {
        route: "/tags",
        icon: IconTags,
        color: "linear-gradient(135deg, #17a2b8, #138496)",
        description: "Organização e categorias",
        category: "organizacao",
      },
      "Meu Perfil": {
        route: "/user-details",
        icon: IconUser,
        color: "linear-gradient(135deg, #6c757d, #495057)",
        description: "Configurações pessoais",
        category: "perfil",
      },
      Suporte: {
        route: "/support",
        icon: IconHeadset,
        color: "linear-gradient(135deg, #007bff, #0056b3)",
        description: "Central de ajuda",
        category: "suporte",
      },
    }),
    []
  );

  // Obter módulos disponíveis para o usuário
  const availableModules = useMemo(() => {
    if (!userPermissions) return [];

    return Object.keys(userPermissions)
      .filter((moduleName) => {
        // Verificar se tem pelo menos permissão de leitura
        return (
          userPermissions[moduleName]?.read === true && moduleConfig[moduleName]
        );
      })
      .map((moduleName) => ({
        name: moduleName,
        ...moduleConfig[moduleName],
        permissions: userPermissions[moduleName],
      }))
      .sort((a, b) => {
        // Ordenar por categoria e depois por nome
        const categoryOrder = {
          principal: 1,
          comunicacao: 2,
          clientes: 3,
          vendas: 4,
          marketing: 5,
          ia: 6,
          automacao: 7,
          organizacao: 8,
          perfil: 9,
          suporte: 10,
        };

        const categoryDiff =
          (categoryOrder[a.category] || 99) - (categoryOrder[b.category] || 99);
        if (categoryDiff !== 0) return categoryDiff;

        return a.name.localeCompare(b.name);
      });
  }, [userPermissions, moduleConfig]);

  // Agrupar módulos por categoria
  const modulesByCategory = useMemo(() => {
    const categories = {};

    availableModules.forEach((module) => {
      if (!categories[module.category]) {
        categories[module.category] = [];
      }
      categories[module.category].push(module);
    });

    return categories;
  }, [availableModules]);

  // Labels das categorias
  const categoryLabels = {
    principal: "Principais",
    comunicacao: "Comunicação",
    clientes: "Clientes",
    vendas: "Vendas",
    marketing: "Marketing",
    ia: "Inteligência Artificial",
    automacao: "Automação",
    organizacao: "Organização",
    perfil: "Perfil",
    suporte: "Suporte",
  };

  // Navegar para um módulo
  const handleModuleClick = (route) => {
    navigate(route);
  };

  // Renderizar card de módulo
  const renderModuleCard = (module) => {
    const IconComponent = module.icon;

    return (
      <Col xs={12} sm={6} lg={4} xl={3} key={module.name} className="mb-4">
        <Card
          className="welcome-module-card h-100"
          onClick={() => handleModuleClick(module.route)}
        >
          <Card.Body className="d-flex flex-column">
            <div className="welcome-module-header">
              <div
                className="welcome-module-icon"
                style={{ background: module.color }}
              >
                <IconComponent size={24} />
              </div>
              <IconArrowRight size={16} className="welcome-module-arrow" />
            </div>

            <h5 className="welcome-module-title">{module.name}</h5>
            <p className="welcome-module-description flex-grow-1">
              {module.description}
            </p>

            <div className="welcome-module-permissions">
              {module.permissions.create && (
                <span className="permission-badge create">Criar</span>
              )}
              {module.permissions.read && (
                <span className="permission-badge read">Ver</span>
              )}
              {module.permissions.update && (
                <span className="permission-badge update">Editar</span>
              )}
              {module.permissions.delete && (
                <span className="permission-badge delete">Excluir</span>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <div className="welcome-container" style={{ paddingTop: 100 }}>
      {/* Header de Boas-vindas */}
      <section className="welcome-header">
        <Container>
          <div className="welcome-hero">
            <div className="welcome-hero-content">
              <div className="welcome-hero-text">
                <h1 className="welcome-title">Olá, {user?.first_name}! 👋</h1>
                <p className="welcome-subtitle">
                  Bem-vindo ao seu espaço de trabalho. Você tem acesso a{" "}
                  <strong>{availableModules.length}</strong> módulo
                  {availableModules.length !== 1 ? "s" : ""} do sistema.
                </p>
              </div>
              <div className="welcome-hero-decoration">
                <div className="welcome-decoration-item">
                  <IconStar size={24} />
                </div>
                <div className="welcome-decoration-item">
                  <IconBulb size={28} />
                </div>
                <div className="welcome-decoration-item">
                  <IconHeart size={20} />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Módulos Disponíveis */}
      <section className="welcome-modules">
        <Container>
          {Object.keys(modulesByCategory).length === 0 ? (
            // Estado vazio
            <div className="welcome-empty-state">
              <Card className="text-center">
                <Card.Body className="py-5">
                  <IconUser size={64} className="text-muted mb-3" />
                  <h4 className="text-muted">Nenhum módulo disponível</h4>
                  <p className="text-muted">
                    Entre em contato com seu administrador para solicitar acesso
                    aos módulos do sistema.
                  </p>
                </Card.Body>
              </Card>
            </div>
          ) : (
            // Renderizar módulos por categoria
            Object.entries(modulesByCategory).map(([category, modules]) => (
              <div key={category} className="welcome-category-section">
                <div className="welcome-category-header">
                  <h2 className="welcome-category-title">
                    {categoryLabels[category] || category}
                  </h2>
                  <span className="welcome-category-count">
                    {modules.length} módulo{modules.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <Row>{modules.map(renderModuleCard)}</Row>
              </div>
            ))
          )}
        </Container>
      </section>

      {/* Footer com dicas */}
      <section className="welcome-footer">
        <Container>
          <Card className="welcome-tips-card">
            <Card.Body>
              <div className="welcome-tips-header">
                <IconBulb size={20} />
                <h5>Dicas para começar</h5>
              </div>
              <Row>
                <Col md={4}>
                  <div className="welcome-tip">
                    <strong>🎯 Comece pelo básico:</strong> Acesse seus módulos
                    principais para se familiarizar com o sistema.
                  </div>
                </Col>
                <Col md={4}>
                  <div className="welcome-tip">
                    <strong>📝 Explore as permissões:</strong> Cada módulo
                    mostra quais ações você pode realizar.
                  </div>
                </Col>
                <Col md={4}>
                  <div className="welcome-tip">
                    <strong>❓ Precisa de ajuda?</strong> Use o módulo de
                    Suporte para obter assistência.
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </section>
    </div>
  );
};

export default WelcomeScreen;
